import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * DemoRequestPage — V22.9.9 public form.
 *
 * Route:  /request-demo
 * Public — no auth required.
 *
 * 6 fields with live business-email validation. Friendly error if a
 * consumer / disposable domain is detected; success state shows a
 * polite "We'll be in touch shortly" message.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, Sparkles, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same static blocklist mirrored client-side so we fail fast before the POST.
// Source of truth lives on the backend.
const CLIENT_BLOCKED_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.in", "yahoo.co.uk", "yahoo.fr", "ymail.com",
  "outlook.com", "hotmail.com", "live.com", "msn.com", "icloud.com", "me.com", "mac.com",
  "aol.com", "aim.com", "protonmail.com", "proton.me", "pm.me", "zoho.com", "zohomail.com",
  "gmx.com", "gmx.net", "gmx.de", "mail.com", "yandex.com", "yandex.ru",
  "tutanota.com", "fastmail.com", "rediffmail.com", "qq.com", "163.com", "126.com",
  "mailinator.com", "guerrillamail.com", "10minutemail.com", "tempmail.com",
  "yopmail.com", "throwaway.email", "trashmail.com",
]);

const isBusinessEmail = (email) => {
  if (!email || !/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(email)) {
    return { ok: false, reason: "Please enter a valid email address." };
  }
  const domain = email.toLowerCase().split("@")[1];
  if (CLIENT_BLOCKED_DOMAINS.has(domain)) {
    return { ok: false, reason: `Please use your work email — we don't accept ${domain} addresses.` };
  }
  return { ok: true };
};

export default function DemoRequestPage() {
  const [form, setForm] = useState({
    requester_name: "",
    job_title: "",
    organization_name: "",
    business_email: "",
    phone_number: "",
    resilience_needs: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onEmailBlur = () => {
    if (!form.business_email) { setEmailError(""); return; }
    const r = isBusinessEmail(form.business_email);
    setEmailError(r.ok ? "" : r.reason);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const r = isBusinessEmail(form.business_email);
    if (!r.ok) { setEmailError(r.reason); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      setSubmitted(true);
    } catch (err) {
      setFormError(extractErrorMessage(err, "Submission failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-sky-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full" data-testid="demo-request-success">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-14 h-14 mx-auto bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Thank you, we'll be in touch shortly
            </h2>
            <p className="text-sm text-slate-600">
              Your demo request has been received. A member of our team will review it within 1 business day
              and email you a personalised welcome with sign-in credentials.
            </p>
            <p className="text-[12px] text-slate-400">
              Questions in the meantime? Reach <a href="mailto:sales@resiliencyhub.ai" className="text-violet-700 underline">sales@resiliencyhub.ai</a>.
            </p>
            <Link to="/">
              <Button variant="outline" data-testid="demo-request-success-home">Back to home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-sky-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px] mb-2">
            <Sparkles className="w-2.5 h-2.5 mr-1" />
            Enterprise Business Continuity Platform
          </Badge>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Request Enquiry &amp; Demo
          </h1>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            Tell us about your resilience programme — we'll provision a tailored ResiliencyHUB workspace
            with sample BIAs, vendor risk registers and recovery plans for your evaluation.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-violet-600" />
              Tell us about you
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-request-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FieldRow label="Requester Name *" testId="demo-field-requester-name">
                  <Input value={form.requester_name} onChange={setField("requester_name")} required placeholder="Jane Doe" data-testid="demo-field-requester-name-input" />
                </FieldRow>
                <FieldRow label="Job Title *" testId="demo-field-job-title">
                  <Input value={form.job_title} onChange={setField("job_title")} required placeholder="Head of Business Continuity" data-testid="demo-field-job-title-input" />
                </FieldRow>
              </div>
              <FieldRow label="Organization Name *" testId="demo-field-organization-name">
                <Input value={form.organization_name} onChange={setField("organization_name")} required placeholder="Acme Insurance Group" data-testid="demo-field-organization-name-input" />
              </FieldRow>
              <FieldRow label="Business Email Address *" testId="demo-field-business-email" error={emailError}>
                <Input
                  type="email"
                  value={form.business_email}
                  onChange={setField("business_email")}
                  onBlur={onEmailBlur}
                  required
                  placeholder="jane.doe@acme-insurance.com"
                  data-testid="demo-field-business-email-input"
                  className={emailError ? "border-rose-400 focus:border-rose-500" : ""}
                />
              </FieldRow>
              <FieldRow label="Phone Number" testId="demo-field-phone-number">
                <Input value={form.phone_number} onChange={setField("phone_number")} placeholder="+1 (555) 123-4567" data-testid="demo-field-phone-number-input" />
              </FieldRow>
              <FieldRow label="Briefly describe your resilience needs *" testId="demo-field-resilience-needs">
                <Textarea
                  value={form.resilience_needs}
                  onChange={setField("resilience_needs")}
                  required
                  rows={4}
                  placeholder="e.g. ISO 22301 certification due in Q4 · 200+ business processes across 4 regions · need to consolidate vendor risk + IT-DRP + BIA on one platform."
                  data-testid="demo-field-resilience-needs-input"
                />
              </FieldRow>

              {formError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs p-3 flex items-start gap-2" data-testid="demo-request-form-error">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <p className="text-[11px] text-slate-500">
                We only accept business email addresses. Personal accounts (gmail, yahoo, outlook…) are filtered automatically.
              </p>

              <Button
                type="submit"
                disabled={submitting || !!emailError}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="demo-request-submit"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Request Demo Access
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-slate-400 mt-4">
          Already have an account? <Link to="/login" className="text-violet-600 hover:underline">Sign in →</Link>
        </p>
      </div>
    </div>
  );
}

const FieldRow = ({ label, children, testId, error }) => (
  <div className="space-y-1" data-testid={testId}>
    <Label className="text-xs font-semibold text-slate-700">{label}</Label>
    {children}
    {error && <p className="text-[11px] text-rose-600 mt-0.5" data-testid={`${testId}-error`}>{error}</p>}
  </div>
);
